/**
 * Overrides لرسائل الهاتف (بوّابة الطلب + أخطاء التحقّق).
 * - getPhoneMessagesOverrides: عامّ (يُقرَأ مع كلّ زائر).
 * - updatePhoneMessagesOverrides: أدمن فقط.
 *
 * التخزين: `app_settings.phone_messages_overrides` كـjsonb:
 *   { "phone.gate.title": { ar, en, fr }, ... }
 *
 * مفاتيح مسموحة فقط: ^phone\\. (حماية).
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "@/utils/adminAuth.server";
import { invalidateAppSettingsCache } from "@/lib/server/appSettings";

const SETTING_KEY = "phone_messages_overrides";

const EntrySchema = z.object({
  ar: z.string().max(800).optional(),
  en: z.string().max(800).optional(),
  fr: z.string().max(800).optional(),
});

const PayloadSchema = z.record(
  z.string().regex(/^phone\.[a-z0-9_.]+$/i).max(100),
  EntrySchema,
);

export type PhoneMessagesOverridesPayload = z.infer<typeof PayloadSchema>;

export const getPhoneMessagesOverrides = createServerFn({ method: "GET" }).handler(
  async (): Promise<PhoneMessagesOverridesPayload> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    try {
      const { data } = await supabaseAdmin
        .from("app_settings")
        .select("value")
        .eq("key", SETTING_KEY)
        .maybeSingle();
      if (!data?.value) return {};
      const parsed = PayloadSchema.safeParse(data.value);
      return parsed.success ? parsed.data : {};
    } catch {
      return {};
    }
  },
);

export const updatePhoneMessagesOverrides = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => PayloadSchema.parse(d))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await assertAdmin(context.userId);
    const cleaned: PhoneMessagesOverridesPayload = {};
    for (const [k, v] of Object.entries(data)) {
      const ar = (v.ar ?? "").trim();
      const en = (v.en ?? "").trim();
      const fr = (v.fr ?? "").trim();
      if (ar || en || fr) {
        cleaned[k] = {
          ...(ar ? { ar } : {}),
          ...(en ? { en } : {}),
          ...(fr ? { fr } : {}),
        };
      }
    }
    const { error } = await supabaseAdmin
      .from("app_settings")
      .upsert({ key: SETTING_KEY, value: cleaned as unknown as never }, { onConflict: "key" });
    if (error) throw new Error(error.message);
    invalidateAppSettingsCache(SETTING_KEY);
    return { ok: true };
  });
